// Build / upgrade order forms + success screen
import { apiFetch } from '../../core/api.js';
import { getTg } from '../../core/telegram.js';
import { setActiveTab, showView } from '../../app/navigation.js';
import { getIsOwnerApp, getIsUpgradeMode } from '../../app/app-state.js';
import { setBuildOrderDraft, type BuildRequestType } from '../../app/build-draft.js';
import { enterBuildCheckout } from '../checkout/mode.js';
import { getEl, getInput, getTextArea, mustEl, mustTextArea } from '../../core/dom.js';
import {
  initComponentsAutocomplete,
  validateComponentSelection,
  formatComponentForOrder
} from './components-autocomplete.js';
import type { ApiResponse } from '../../types/api.js';

initComponentsAutocomplete();

function buildSubmitErrorMessage(data: ApiResponse | { error?: string } | null): string {
  if (data && 'error' in data && data.error) return String(data.error);
  return 'Не удалось отправить заявку. Попробуйте позже.';
}

const buildTasks = mustTextArea('build-tasks');
const buildTasksErr = mustEl('build-tasks-error');
buildTasks.addEventListener('input', () => {
  buildTasks.classList.remove('form-textarea-error');
  buildTasksErr.textContent = '';
});

const COMP_FIELDS: { id: string; label: string }[] = [
  { id: 'comp-cpu', label: 'CPU' },
  { id: 'comp-gpu', label: 'GPU' },
  { id: 'comp-motherboard', label: 'Мат. плата' },
  { id: 'comp-ram', label: 'RAM' },
  { id: 'comp-ssd', label: 'SSD' },
  { id: 'comp-psu', label: 'БП' },
  { id: 'comp-case', label: 'Корпус' },
  { id: 'comp-cooling', label: 'Охлаждение' }
];
COMP_FIELDS.forEach(({ id }) => {
  const el = getInput(id);
  const errId = id + '-error';
  if (el)
    el.addEventListener('input', () => {
      el.classList.remove('form-input-error');
      const err = getEl(errId);
      if (err) err.textContent = '';
    });
});

function setBuildSuccessPhase(phase: 'loading' | 'done'): void {
  const stack = getEl('build-success-stack');
  const loadingEl = getEl('build-success-loading');
  const doneEl = getEl('build-success-done');
  if (!stack || !loadingEl || !doneEl) return;
  if (phase === 'loading') {
    stack.classList.remove('build-success-stack--state-done');
    stack.classList.add('build-success-stack--state-loading');
    loadingEl.setAttribute('aria-hidden', 'false');
    doneEl.setAttribute('aria-hidden', 'true');
  } else {
    stack.classList.remove('build-success-stack--state-loading');
    stack.classList.add('build-success-stack--state-done');
    loadingEl.setAttribute('aria-hidden', 'true');
    doneEl.setAttribute('aria-hidden', 'false');
  }
}

var BUILD_ORDER_MIN_MS = 2000;
var BUILD_REQUEST_LIMIT = 3;
var BUILD_REQUEST_WINDOW_MS = 5 * 60 * 1000;
var BUILD_REQUEST_RATE_MSG = 'Можно отправить не больше 3 заявок за 5 минут.';

function readBuildRequestTimestamps(): number[] {
  var now = Date.now();
  var arr: number[] = [];
  try {
    var raw = localStorage.getItem('buildRequestTimestamps');
    if (raw) {
      var parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed as number[];
    }
  } catch (_) {}
  if (!arr.length) {
    var legacy = localStorage.getItem('buildRequestLastAt');
    var ts = legacy ? parseInt(legacy, 10) : 0;
    if (ts) arr = [ts];
  }
  return arr.filter(function (t: number) {
    return now - Number(t) < BUILD_REQUEST_WINDOW_MS;
  });
}

function isBuildRequestRateLimited(): boolean {
  return readBuildRequestTimestamps().length >= BUILD_REQUEST_LIMIT;
}

function recordBuildRequestSubmit(): void {
  var arr = readBuildRequestTimestamps();
  arr.push(Date.now());
  localStorage.setItem('buildRequestTimestamps', JSON.stringify(arr));
}

function applyBuildRequestRateLimitUi(alertEl: HTMLElement): boolean {
  if (getIsOwnerApp()) {
    alertEl.classList.remove('alert-banner-visible');
    alertEl.textContent = '';
    return false;
  }
  if (isBuildRequestRateLimited()) {
    alertEl.textContent = BUILD_REQUEST_RATE_MSG;
    alertEl.classList.add('alert-banner-visible');
    return true;
  }
  alertEl.classList.remove('alert-banner-visible');
  alertEl.textContent = '';
  return false;
}

function waitUntilMinElapsed(minMs: number, startedAt: number): Promise<void> {
  return new Promise<void>(function (resolve) {
    setTimeout(resolve, Math.max(0, minMs - (Date.now() - startedAt)));
  });
}

function goToBuildCheckout(tasks: string, requestType: BuildRequestType, label: string): void {
  setBuildOrderDraft({ tasks, requestType, label });
  enterBuildCheckout();
  showView('view-checkout', 'right');
}

mustEl<HTMLButtonElement>('build-submit-btn').addEventListener('click', () => {
  const alertEl = mustEl('build-rate-limit-alert');
  if (applyBuildRequestRateLimitUi(alertEl)) return;

  const tasks = buildTasks.value.trim();
  const isUpgradeMode = !!getIsUpgradeMode();
  let hasError = false;

  if (!tasks) {
    buildTasks.classList.add('form-textarea-error');
    buildTasksErr.textContent = isUpgradeMode ? 'Заполните поле.' : 'Заполните поле «Задачи и бюджет».';
    hasError = true;
  } else if (tasks.length < 10) {
    buildTasks.classList.add('form-textarea-error');
    buildTasksErr.textContent = 'Сообщение должно содержать минимум 10 символов.';
    hasError = true;
  } else {
    buildTasks.classList.remove('form-textarea-error');
    buildTasksErr.textContent = '';
  }

  if (hasError) return;

  const requestType: BuildRequestType = isUpgradeMode ? 'upgrade' : 'build';
  const label = isUpgradeMode ? 'Апгрейд ПК' : 'Сборка с подбором';
  goToBuildCheckout(tasks, requestType, label);
});

mustEl<HTMLButtonElement>('components-submit-btn').addEventListener('click', () => {
  const alertEl = mustEl('components-rate-limit-alert');
  if (applyBuildRequestRateLimitUi(alertEl)) return;

  let hasError = false;
  COMP_FIELDS.forEach(({ id, label }) => {
    const check = validateComponentSelection(id, label);
    if (!check.ok) hasError = true;
  });

  if (hasError) return;

  const parts: string[] = [];
  COMP_FIELDS.forEach(({ id, label }) => {
    parts.push(formatComponentForOrder(id, label));
  });
  const compWishes = getTextArea('comp-wishes');
  const wishes = (compWishes && compWishes.value) || '';
  if (wishes.trim()) parts.push('Пожелания: ' + wishes.trim());
  const tasks = parts.join('\n');

  goToBuildCheckout(tasks, 'components', 'Сборка по списку');
});

mustEl<HTMLButtonElement>('success-home-btn').addEventListener('click', () => {
  showView('view-main');
});

const successOrdersBtn = getEl<HTMLButtonElement>('success-orders-btn');
if (successOrdersBtn) {
  successOrdersBtn.addEventListener('click', () => {
    setActiveTab(document.getElementById('tab-profile'));
    showView('view-profile', 'left');
  });
}

export { setBuildSuccessPhase, waitUntilMinElapsed, recordBuildRequestSubmit, buildSubmitErrorMessage };
